import '../css/EditRunConfigModal.less';

import React, {useCallback, useRef, useState} from 'react';
import {Button, Modal} from 'semantic-ui-react';
import Editor from './Monaco/Editor';
import {navigateTo} from '../util/history';
import {projectRunQueue} from '../util/urls';
import WandbLoader from './WandbLoader';
import {useLoadFile} from '../util/requests';
import {
  useCreateRunQueueMutation,
  usePushToRunQueueMutation,
} from '../generated/graphql';
import makeComp from '../util/profiler';

interface EditRunConfigModalProps {
  entityName: string;
  projectName: string;
  queueID?: string;
  runName: string;
  onClose: () => void;
}

const EditRunConfigModal: React.FC<EditRunConfigModalProps> = makeComp(
  ({entityName, projectName, queueID, runName, onClose}) => {
    const [createRunQueue] = useCreateRunQueueMutation();
    const [pushToRunQueue] = usePushToRunQueueMutation();

    const [runConfig, setRunConfig] = useState<string | null>(null);
    const [pushing, setPushing] = useState(false);
    const initialConfigRef = useRef<string | null>(null);

    const setInitialRunConfig = useCallback((val: string) => {
      setRunConfig(val);
      initialConfigRef.current = val;
    }, []);

    const isChanged = () => runConfig !== initialConfigRef.current;

    useLoadFile({entityName, projectName, runName}, 'wandb-jobspec.json', {
      onSuccess: setInitialRunConfig,
    });

    const pushRunToQueue = async () => {
      if (runConfig == null || pushing) {
        return;
      }
      setPushing(true);
      let queueCreated = false;
      if (queueID === undefined) {
        // Redundant condition for the benefit of the type checker.
        const {data} = await createRunQueue({
          variables: {
            entityName,
            projectName,
            queueName: 'asdf',
          },
        });
        if (
          !data?.createRunQueue?.success ||
          data?.createRunQueue?.queueId == null
        ) {
          alert('Run Queue creation failed');
          setPushing(false);
          return;
        }

        queueID = data?.createRunQueue?.queueId;
        queueCreated = true;
      }
      await pushToRunQueue({
        variables: {queueId: queueID, runSpec: runConfig},
      });
      if (queueCreated) {
        window.location.assign(
          projectRunQueue({entityName, name: projectName})
        );
      } else {
        navigateTo({
          pathname: projectRunQueue({entityName, name: projectName}),
          preserveSearch: true,
        });
      }
    };

    return (
      <Modal open={true} className="edit-run-config-modal" onClose={onClose}>
        <Modal.Header>Modify your run config</Modal.Header>
        <Modal.Content>
          <div className="editor-wrapper">
            {runConfig == null ? (
              <WandbLoader />
            ) : (
              <Editor
                value={runConfig}
                onChange={setRunConfig}
                height={500}
                language="json"
                theme="vs-dark"
              />
            )}
          </div>
        </Modal.Content>
        <Modal.Actions>
          <Button size="tiny" disabled={pushing} onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="tiny"
            primary
            disabled={!isChanged() || pushing}
            loading={pushing}
            onClick={pushRunToQueue}>
            Push Run
          </Button>
        </Modal.Actions>
      </Modal>
    );
  },
  {id: 'EditRunConfigModal'}
);

export default EditRunConfigModal;
